import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await authAPI.getMe();
      setUser(response.data.user);
      setProfile(response.data.profile);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, user: userData, profile: profileData, requires_2fa, temp_token } = response.data;
    
    // Handle 2FA requirement
    if (requires_2fa && !access_token) {
      setRequires2FA(true);
      setTempToken(temp_token);
      setPendingUser(userData);
      return { requires_2fa: true, user: userData };
    }
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('profile', JSON.stringify(profileData));
    
    setUser(userData);
    setProfile(profileData);
    setIsAuthenticated(true);
    setRequires2FA(false);
    setTempToken(null);
    setPendingUser(null);
    
    return response.data;
  };

  const verify2FA = async (code) => {
    if (!tempToken) {
      throw new Error('No pending 2FA verification');
    }
    
    const response = await authAPI.verify2FA({ temp_token: tempToken, code });
    const { access_token, user: userData, profile: profileData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('profile', JSON.stringify(profileData));
    
    setUser(userData);
    setProfile(profileData);
    setIsAuthenticated(true);
    setRequires2FA(false);
    setTempToken(null);
    setPendingUser(null);
    
    return response.data;
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setTempToken(null);
    setPendingUser(null);
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    const { access_token, user: userData, profile: profileData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('profile', JSON.stringify(profileData));
    
    setUser(userData);
    setProfile(profileData);
    setIsAuthenticated(true);
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('profile');
    setUser(null);
    setProfile(null);
    setIsAuthenticated(false);
  };

  const updateProfile = (newProfile) => {
    setProfile(newProfile);
    localStorage.setItem('profile', JSON.stringify(newProfile));
  };

  // Role helpers
  const isCarrier = user?.role && ['carrier', 'owner_operator', 'fleet', 'dispatcher'].includes(user.role);
  const isBroker = user?.role && ['broker', 'shipper', 'dealer', 'auction', 'rental_fleet_owner'].includes(user.role);
  const isAdmin = user?.role === 'admin';
  const canPostLoads = isBroker || isAdmin;
  const canRequestLoads = isCarrier || isAdmin;
  
  // Get current token
  const token = localStorage.getItem('token');

  const value = {
    user,
    profile,
    loading,
    isAuthenticated,
    token,
    login,
    register,
    logout,
    updateProfile,
    loadUser,
    isCarrier,
    isBroker,
    isAdmin,
    canPostLoads,
    canRequestLoads,
    requires2FA,
    tempToken,
    pendingUser,
    verify2FA,
    cancel2FA,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
